import {
	AttendanceArraysMutationResult,
	AttendanceMutationRow,
	NotificationPrefsPatch,
	WaitListMutationRow,
	addWaitListEntry,
	dedupeAttendanceMember,
	removeWaitListMember,
	syncAttendanceMemberName,
	syncAttendanceNotificationName,
	syncAttendanceNotificationPrefs,
	syncScheduleNotificationsArray,
} from './attendance-mutations';
import { removeAttendanceWithWaitListPromotion } from './schedule-attendance-remove';
import type { ScheduleRawArrays } from './schedule-atomic-merge';
import { DEFAULT_SCHEDULE_RESERVE_LIMIT } from './schedule-reserve-limit';
import type { PromotionResultAttendanceRow, WaitListPromotableRow } from './wait-list-promotion';
import {
	ScheduleAtCapacityReason,
	upsertAttendanceMemberRespectingCapacity,
} from './schedule-attendance-upsert';

export type ScheduleAttendanceMutationMode =
	| 'upsert'
	| 'remove'
	| 'waitListAdd'
	| 'waitListRemove'
	| 'dedupeMember'
	| 'syncMemberName'
	| 'syncNotificationName'
	| 'syncNotificationPrefs';

export interface ScheduleAttendanceMutationRequest {
	mode: ScheduleAttendanceMutationMode;
	scheduleId: string;
	memberId: string;
	reserveLimit?: number;
	memberName?: string;
	notifications?: unknown;
	attended?: boolean;
	reserved?: boolean;
	lessonCategory?: string;
	waitListEntry?: WaitListMutationRow;
	scheduleNotifications?: readonly NotificationPrefsPatch[];
}

/** Wait-list rows for atomic mutations must include `name` (FIFO promotion). */
export type ScheduleAttendanceWaitListRow = WaitListMutationRow & WaitListPromotableRow;

export type ScheduleAttendanceMutationResult<
	TAtt extends AttendanceMutationRow,
	TWait extends ScheduleAttendanceWaitListRow,
> = AttendanceArraysMutationResult<TAtt | PromotionResultAttendanceRow, TWait> & {
	rejected?: ScheduleAtCapacityReason;
	scheduleNotifications?: NotificationPrefsPatch[];
};

/**
 * Pure domain entry for all `enrollInScheduleAtomic` modes (controller supplies `reserveLimit` from program).
 */
export function applyScheduleAttendanceMutation<
	TAtt extends AttendanceMutationRow,
	TWait extends ScheduleAttendanceWaitListRow,
>(
	attendance: readonly TAtt[],
	waitList: readonly TWait[],
	request: ScheduleAttendanceMutationRequest,
): ScheduleAttendanceMutationResult<TAtt, TWait> {
	const reserveLimit = request.reserveLimit ?? DEFAULT_SCHEDULE_RESERVE_LIMIT;

	switch (request.mode) {
		case 'remove':
			return removeAttendanceWithWaitListPromotion(
				request.scheduleId,
				attendance,
				waitList,
				request.memberId,
				reserveLimit,
			);
		case 'waitListAdd':
			if (!request.waitListEntry) {
				return { attendance: [...attendance], waitList: [...waitList], changed: false };
			}
			{
				const added = addWaitListEntry(waitList, request.waitListEntry as TWait);
				return { attendance: [...attendance], waitList: added.waitList, changed: added.changed };
			}
		case 'waitListRemove': {
			const removed = removeWaitListMember(waitList, request.memberId);
			return { attendance: [...attendance], waitList: removed.waitList, changed: removed.changed };
		}
		case 'dedupeMember': {
			const deduped = dedupeAttendanceMember(attendance, request.memberId, {
				memberName: request.memberName,
				notifications: request.notifications,
			});
			return { attendance: deduped.attendance, waitList: [...waitList], changed: deduped.changed };
		}
		case 'syncMemberName': {
			const synced = syncAttendanceMemberName(attendance, request.memberId, request.memberName);
			return { attendance: synced.attendance, waitList: [...waitList], changed: synced.changed };
		}
		case 'syncNotificationName': {
			const synced = syncAttendanceNotificationName(attendance, request.memberId, request.memberName);
			return { attendance: synced.attendance, waitList: [...waitList], changed: synced.changed };
		}
		case 'syncNotificationPrefs': {
			const patch = request.notifications as NotificationPrefsPatch | undefined;
			if (!patch?.id) {
				return { attendance: [...attendance], waitList: [...waitList], changed: false };
			}
			const attendanceSynced = syncAttendanceNotificationPrefs(attendance, request.memberId, patch);
			const scheduleSynced = syncScheduleNotificationsArray(request.scheduleNotifications, request.memberId, patch);
			return {
				attendance: attendanceSynced.attendance,
				waitList: [...waitList],
				scheduleNotifications: scheduleSynced.notifications,
				changed: attendanceSynced.changed || scheduleSynced.changed,
			};
		}
		default: {
			const upserted = upsertAttendanceMemberRespectingCapacity(
				attendance,
				request.memberId,
				request.scheduleId,
				reserveLimit,
				{
					attended: request.attended,
					memberName: request.memberName,
					notifications: request.notifications,
					reserved: request.reserved,
					lessonCategory: request.lessonCategory,
				},
			);
			return {
				attendance: upserted.attendance,
				waitList: [...waitList],
				changed: upserted.changed,
				rejected: upserted.rejected,
			};
		}
	}
}

/** Schedule document fields updated by preview / atomic attendance mutations. */
export type ScheduleAttendanceRaw<TAtt = unknown, TWait = unknown, TNotif = unknown> = ScheduleRawArrays<
	TAtt,
	TWait
> & {
	notifications?: TNotif[];
};

/**
 * Applies {@link applyScheduleAttendanceMutation} to in-memory schedule `raw` (preview / UI simulation).
 * Authoritative writes use `enrollInScheduleAtomic` + {@link applyScheduleAtomicPatchToRaw}.
 */
export function applyScheduleAttendanceMutationToRaw<
	TAtt extends AttendanceMutationRow,
	TWait extends ScheduleAttendanceWaitListRow,
	TNotif extends NotificationPrefsPatch,
>(
	raw: ScheduleAttendanceRaw<TAtt, TWait, TNotif>,
	request: ScheduleAttendanceMutationRequest,
): ScheduleAttendanceMutationResult<TAtt, TWait> {
	const result = applyScheduleAttendanceMutation(raw.attendance ?? [], raw.waitList ?? [], request);

	if (result.changed) {
		raw.attendance = result.attendance as TAtt[];
		raw.waitList = result.waitList;
		if (result.scheduleNotifications !== undefined) {
			raw.notifications = result.scheduleNotifications as TNotif[];
		}
	}

	return result;
}
